/**
 * OmegaLogPage — raw engine-log viewer (`/omega/log`).
 *
 * Newest-first stream of signed log entries with a type filter and a
 * 5-second auto-refresh via TanStack Query hitting the live
 * `GET /api/v1/omega/log/tail` endpoint.
 * Click a row to expand the full payload.
 */

import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { cn } from '@/utils/cn';
import { apiFetch } from '@/api/http';

const REFETCH_MS = 5_000;

// ── Wire types matching omega_log.rs OmegaLogEntryWire ────────────────────────

interface OmegaLogEntry {
  seq: number;
  entry_type: string;
  payload_hash: string;
  payload_json: Record<string, unknown>;
  signature: string;
}

interface LogTailResponse {
  entries: OmegaLogEntry[];
  next_seq: number;
  total: number;
}

function fetchLogTail(limit: number): Promise<LogTailResponse> {
  return apiFetch<LogTailResponse>(`/omega/log/tail?limit=${limit}`);
}

export function OmegaLogPage() {
  if (!isOmegaAdmin()) {
    return <Navigate to="/" replace />;
  }
  return <LogPageInner />;
}

function LogPageInner() {
  const [filter, setFilter] = useState<string>('all');
  const [expanded, setExpanded] = useState<number | null>(null);

  const { data, isPending, isError, dataUpdatedAt } = useQuery<LogTailResponse>({
    queryKey: ['omega', 'log', 'tail', 200],
    queryFn: () => fetchLogTail(200),
    refetchInterval: REFETCH_MS,
    staleTime: REFETCH_MS - 1_000,
  });

  const allEntries = data?.entries ?? [];

  // Collect unique entry_type values from the live data.
  const entryTypes = Array.from(new Set(allEntries.map((e) => e.entry_type))).sort();

  const filtered = allEntries
    .filter((e) => filter === 'all' || e.entry_type === filter)
    .slice()
    .sort((a, b) => b.seq - a.seq);

  const lastUpdated = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: 'Log' },
        ]}
      />

      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Engine log</h1>
        <p className="text-sm text-ink-muted">
          Raw signed entries from the OMEGA log. Newest first. Auto-refreshing every 5s.
          {lastUpdated ? (
            <> <span className="font-mono text-ink-subtle">last updated {lastUpdated}</span></>
          ) : null}
          {data?.total != null ? (
            <> · <span className="font-mono text-ink-subtle">{data.total} total entries</span></>
          ) : null}
        </p>
      </header>

      <section className="rounded-xl border border-border bg-surface p-6">
        <div className="flex flex-wrap items-center gap-3">
          <label className="text-[11px] uppercase tracking-wider text-ink-subtle">
            Filter by type
          </label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-md border border-border bg-canvas px-3 py-2 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-spark"
          >
            <option value="all">All types</option>
            {entryTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <span className="ms-auto text-xs text-ink-muted">
            {isPending ? 'Loading…' : `${filtered.length} entr${filtered.length === 1 ? 'y' : 'ies'}`}
          </span>
        </div>

        {isError && (
          <div className="mt-4 rounded-md border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">
            Failed to load log from server. The API server may not be running.
          </div>
        )}

        {!isError && !isPending && filtered.length === 0 ? (
          <p className="mt-5 rounded-md border border-dashed border-border bg-canvas p-4 text-sm text-ink-muted">
            No log entries match this filter.
          </p>
        ) : (
          !isError && (
            <div className="mt-5 overflow-hidden rounded-md border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-raised text-[11px] uppercase tracking-wider text-ink-subtle">
                    <th className="w-16 px-3 py-2 text-start font-medium">Seq</th>
                    <th className="px-3 py-2 text-start font-medium">Type</th>
                    <th className="px-3 py-2 text-start font-medium">Hash</th>
                    <th className="px-3 py-2 text-start font-medium">Signature</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => (
                    <Row
                      key={e.seq}
                      entry={e}
                      isExpanded={expanded === e.seq}
                      onToggle={() => setExpanded((cur) => (cur === e.seq ? null : e.seq))}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}
      </section>
    </div>
  );
}

function Row({
  entry,
  isExpanded,
  onToggle,
}: {
  entry: OmegaLogEntry;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const hashShort = entry.payload_hash.startsWith('sha256:')
    ? `sha256:${entry.payload_hash.slice(7, 15)}…`
    : `${entry.payload_hash.slice(0, 10)}…`;
  const sigShort = entry.signature.startsWith('ed25519:')
    ? `ed25519:${entry.signature.slice(8, 16)}…`
    : entry.signature.slice(0, 12);

  return (
    <>
      <tr
        className={cn(
          'cursor-pointer border-t border-border hover:bg-surface-raised/50',
          isExpanded && 'bg-surface-raised/30',
        )}
        onClick={onToggle}
      >
        <td className="px-3 py-2 font-mono text-xs text-ink-muted">{entry.seq}</td>
        <td className="px-3 py-2 font-mono text-xs text-ink">{entry.entry_type}</td>
        <td className="px-3 py-2 font-mono text-xs text-ink-muted">{hashShort}</td>
        <td className="px-3 py-2 font-mono text-xs text-ink-muted">{sigShort}</td>
      </tr>
      {isExpanded ? (
        <tr className="border-t border-border bg-canvas">
          <td colSpan={4} className="px-3 py-3">
            <dl className="grid grid-cols-1 gap-2 sm:grid-cols-[140px_1fr] text-xs">
              <dt className="text-ink-subtle">Sequence</dt>
              <dd className="font-mono text-ink">{entry.seq}</dd>
              <dt className="text-ink-subtle">Type</dt>
              <dd className="font-mono text-ink">{entry.entry_type}</dd>
              <dt className="text-ink-subtle">Hash</dt>
              <dd className="break-all font-mono text-ink-muted">{entry.payload_hash}</dd>
              <dt className="text-ink-subtle">Signature</dt>
              <dd className="break-all font-mono text-ink-muted">{entry.signature}</dd>
              <dt className="text-ink-subtle">Payload</dt>
              <dd>
                <pre className="overflow-x-auto rounded-md bg-surface p-3 font-mono text-[11px] text-ink-muted">
                  {JSON.stringify(entry.payload_json, null, 2)}
                </pre>
              </dd>
            </dl>
          </td>
        </tr>
      ) : null}
    </>
  );
}

export default OmegaLogPage;
